import {
  BoxProps,
  Button,
  ButtonProps,
  color,
  Dropdown as DsUiDropdown,
  Flex,
  IconButton,
  spacing,
  StyleProps,
  Text,
  ThreeDotsVerticalSvg,
  typography,
  useColorMode,
  useDisclosure,
  useIsMobileView,
} from 'deepstash-ui';
import dynamic from 'next/dynamic';
import React, { useState } from 'react';

const GetTheAppModal = dynamic(
  () => import('src/components/modals/get-the-app-modal/GetTheAppModal'),
);

export type DropdownEntry = {
  title: string;
  icon?: React.ReactElement;
  callback: () => void;
  /**
   * If this property is present, the action for this button is not allowed on mobile.
   * Instead, it will open the GetTheApp modal with this title passed as props
   */
  getTheAppTitle?: string;
  titleStyling?: StyleProps;
};

interface DropdownProps {
  items: DropdownEntry[];
  /**
   * prop passed to the DS-UI dropdown, for the container styling
   */
  wrapperStyle?: StyleProps;
  itemId?: string;
  /**
   * The icon to use for the dropdwon button
   * If none is specified, the default 3-dots will be used
   */
  icon?: React.ReactElement;
  /**
   * When this is true, the background of the dropdown button will darken on hover
   * @Default True
   */
  withHoverEffect?: boolean;
  backgroundColor?: string;
  menuStyle?: BoxProps;
  dropdownMode?: 'fixed' | 'absolute';
  dropdownButtonStyle?: ButtonProps;
}

const Dropdown: React.FC<DropdownProps> = ({
  items,
  wrapperStyle,
  itemId,
  icon,
  withHoverEffect = true,
  backgroundColor,
  menuStyle,
  dropdownMode,
  dropdownButtonStyle,
}) => {
  const { colorMode } = useColorMode();
  const [getTheAppModalTitle, setGetTheAppModalTitle] = useState('');
  const isMobileView = useIsMobileView();

  const {
    isOpen: isGetTheAppModalOpen,
    onOpen: onGetTheAppModalOpen,
    onClose: onGetTheAppModalClose,
  } = useDisclosure();

  const renderDropdownButton = () => (
    <IconButton
      data-testid="insight-dropdown-menu-button"
      _focus={undefined}
      backgroundColor={color.transparent}
      aria-label="Options"
      px={spacing.XS.rem}
      _hover={undefined}
      _active={undefined}
      onClick={() => {
        return;
      }}
      {...dropdownButtonStyle}
    >
      {icon ?? (
        <ThreeDotsVerticalSvg boxSize="24px" color={color[colorMode].text} />
      )}
    </IconButton>
  );

  return (
    <DsUiDropdown
      wrapperButtonStyle={{
        _hover: withHoverEffect
          ? {
              backgroundColor: color[colorMode].surface,
              borderRadius: 'full',
              cursor: 'pointer',
            }
          : undefined,
        _active: {
          backgroundColor: color[colorMode].surface,
          borderRadius: 'full',
          cursor: 'pointer',
        },
        _focus: undefined,
        _pressed: undefined,
      }}
      dropdownButtonRenderer={renderDropdownButton}
      menuStyle={{
        background: backgroundColor ?? color[colorMode].background,
        minWidth: '14rem',
        ...menuStyle,
      }}
      wrapperStyle={wrapperStyle}
      dropdownMode={dropdownMode}
    >
      {onClose => (
        <Flex
          border="none"
          padding="8px 0"
          borderRadius="10px"
          shadow={undefined}
          _hover={undefined}
          zIndex="10"
          maxW={spacing.toRem(270)}
          flexDir="column"
        >
          {items.map(
            (item, index) =>
              item && (
                <Button
                  key={index}
                  backgroundColor={
                    backgroundColor ?? color[colorMode].background
                  }
                  _hover={{ bg: color[colorMode].top }}
                  {...typography.primaryTextFonts.medium.regular}
                  width="100%"
                  color={color[colorMode].textSecondary}
                  borderRadius={spacing.S.rem}
                  onClick={e => {
                    e.stopPropagation();
                    if (isMobileView && item.getTheAppTitle) {
                      setGetTheAppModalTitle(item.getTheAppTitle);
                      onGetTheAppModalOpen();
                    } else {
                      item.callback();
                    }
                    onClose();
                  }}
                  height="fit-content"
                  minHeight="2.5rem"
                  id={itemId ? `${itemId}-button` : undefined}
                >
                  <Flex
                    width="100%"
                    alignItems="center"
                    id={itemId ? `${itemId}-content-wrapper` : undefined}
                  >
                    {item.icon ?? null}
                    <Text
                      paddingLeft="20px"
                      w="fit-content"
                      color={color[colorMode].textSecondary}
                      data-testid="idea-dropdown-option"
                      id={itemId ? `${itemId}-title` : undefined}
                      {...item.titleStyling}
                    >
                      {item.title}
                    </Text>
                  </Flex>
                </Button>
              ),
          )}
          {isGetTheAppModalOpen && (
            <GetTheAppModal
              isOpen={isGetTheAppModalOpen}
              onClose={onGetTheAppModalClose}
              title={getTheAppModalTitle}
            />
          )}
        </Flex>
      )}
    </DsUiDropdown>
  );
};

export default Dropdown;
